// crewbit-admin/src/components/schedule/TeamRow.tsx
//
// Schedule Module Phase C C5B — per-resource scoreboard row (admin-only).
// Spec: docs/references/schedule/SCHEDULE_MODULE_SPEC_v1.md §6.3.
//
// Renders: name + booked/available hours + utilization bar (color-thresholded
// per spec) + top-3 upcoming preview. Acts as a drop target via @dnd-kit
// useDroppable with id `team-row-{userId}` — preserves the drop-target grammar
// the existing index.tsx onDragEnd already handles. Drag-drop reassigns
// entries to this operator (existing handleEntryDrop path).

import { useDroppable } from '@dnd-kit/core';
import { palette } from '../../styles/tokens';
import type { ScheduleEntry } from '../../types';
import { instantToBusinessParts, type ScheduleUser } from './scheduleHelpers';
import type { TeamUtilizationRow } from '../../hooks/useTeamUtilization';

const ROW_HEIGHT = 64;

function formatHours(minutes: number): string {
  const hours = minutes / 60;
  if (hours === Math.floor(hours)) return `${hours}h`;
  return `${hours.toFixed(1)}h`;
}

function utilizationColor(util: number | null): { bar: string; bg: string; label: string } {
  if (util === null) return { bar: palette.g300, bg: palette.g100, label: palette.g500 };
  const pct = util * 100;
  if (pct < 60) return { bar: palette.green, bg: palette.greenWash, label: palette.green };
  if (pct < 85) return { bar: palette.accent, bg: palette.accentWash, label: palette.accent };
  if (pct <= 100) return { bar: palette.amber, bg: palette.amberWash, label: palette.amber };
  return { bar: palette.red, bg: palette.redWash, label: palette.red };
}

function utilizationLabel(util: number | null): string {
  if (util === null) return 'No shift';
  return `${Math.round(util * 100)}%`;
}

export interface TeamRowProps {
  resource: ScheduleUser;
  utilization: TeamUtilizationRow | undefined;
  onEntryClick: (entry: ScheduleEntry) => void;
}

export function TeamRow({ resource, utilization, onEntryClick }: TeamRowProps) {
  const { setNodeRef, isOver } = useDroppable({ id: `team-row-${resource.id}` });
  const u = utilization ?? { bookedMinutes: 0, availableMinutes: 0, utilization: null, upcoming: [] };
  const colors = utilizationColor(u.utilization);
  const fillPct = u.utilization === null ? 0 : Math.min(u.utilization * 100, 100);
  const overflow = u.utilization !== null && u.utilization > 1;

  const name = `${resource.first_name ?? ''} ${resource.last_name ?? ''}`.trim() || 'Operator';

  return (
    <div
      ref={setNodeRef}
      style={{
        display: 'grid',
        gridTemplateColumns: '180px 110px 1fr 240px',
        alignItems: 'center',
        gap: 12,
        height: ROW_HEIGHT,
        padding: '0 14px',
        borderBottom: `1px solid ${palette.g150}`,
        background: isOver ? palette.accentWash : palette.white,
        transition: 'background 120ms ease',
      }}
    >
      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: palette.g800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {name}
        </div>
        <div style={{ fontSize: 11, color: palette.g500, textTransform: 'capitalize' }}>
          {resource.role || resource.employment_type}
        </div>
      </div>

      <div>
        <div style={{ fontSize: 13, fontWeight: 600, color: palette.g800 }}>
          {formatHours(u.bookedMinutes)} <span style={{ color: palette.g500, fontWeight: 400 }}>/ {formatHours(u.availableMinutes)}</span>
        </div>
        <div style={{ fontSize: 11, color: colors.label, fontWeight: 600 }}>
          {utilizationLabel(u.utilization)}
          {overflow ? ' · over' : ''}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1, height: 8, borderRadius: 4, background: colors.bg, overflow: 'hidden' }}>
          <div
            style={{
              height: '100%',
              width: `${fillPct}%`,
              background: colors.bar,
              transition: 'width 200ms ease',
            }}
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, overflowX: 'auto' }}>
        {u.upcoming.length === 0 ? (
          <span style={{ fontSize: 11, color: palette.g500, fontStyle: 'italic' }}>No upcoming</span>
        ) : (
          u.upcoming.map((e) => {
            const start = instantToBusinessParts(e.start_at); // A2 P4 — business tz
            const h12 = start.hour % 12 || 12;
            const m = start.minute;
            const ampm = start.hour >= 12 ? 'p' : 'a';
            const tlabel = m === 0 ? `${h12}${ampm}` : `${h12}:${String(m).padStart(2, '0')}${ampm}`;
            return (
              <button
                key={e.id}
                onClick={() => onEntryClick(e)}
                style={{
                  flexShrink: 0,
                  border: `1px solid ${palette.g200}`,
                  background: palette.white,
                  borderRadius: 6,
                  padding: '4px 8px',
                  fontFamily: 'inherit',
                  fontSize: 11,
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 1,
                  minWidth: 64,
                }}
              >
                <span style={{ fontWeight: 600, color: palette.g800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 80 }}>
                  {e.title}
                </span>
                <span style={{ color: palette.g500 }}>{tlabel}</span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
